import type { GhGroup, KnownApp } from "@kwp/contracts";

/**
 * GitHub as one source in the inbox (architecture §6.7, WP-062): under Notifications, a GitHub
 * item opens `?app=github`, its groups `&gh=…`, and inside a group one filter, PRs or issues
 * (`&item=`) or a repository (`&repo=`), each with its own index.
 */
export const GH_GROUP_VIEWS = [
  { key: "needs-me", label: "Needs me" },
  { key: "conversation", label: "Conversation" },
  { key: "activity", label: "Activity" },
  { key: "ci", label: "CI" },
  { key: "security", label: "Security" },
] as const satisfies ReadonlyArray<{ key: GhGroup; label: string }>;

export type GhItemFilter = "pr" | "issue";

/** The source the list is narrowed to; `item` and `repo` only inside a group, one at a time. */
export interface SourceView {
  app: KnownApp;
  gh: GhGroup | undefined;
  item: GhItemFilter | undefined;
  repo: string | undefined;
}

/** The source view the URL names, or null for the inbox as it was. A repository wins over an item filter. */
export function sourceViewOf(search: { app?: KnownApp | undefined; gh?: GhGroup | undefined; item?: GhItemFilter | undefined; repo?: string | undefined }): SourceView | null {
  if (search.app === undefined) return null;
  const gh = search.gh;
  const repo = gh === undefined ? undefined : search.repo;
  return { app: search.app, gh, repo, item: gh === undefined || repo !== undefined ? undefined : search.item };
}

export function groupLabel(group: GhGroup): string {
  return (GH_GROUP_VIEWS.find((g) => g.key === group) as (typeof GH_GROUP_VIEWS)[number]).label;
}

/** The view as search terms, after `in:inbox` (for "select everything matching"). */
export function sourceQuery(view: SourceView): string {
  const terms = [view.gh === undefined ? `from-app:${view.app}` : `gh:${view.gh}`];
  if (view.repo !== undefined) terms.push(`repo:${view.repo}`);
  if (view.item !== undefined) terms.push(`is:${view.item}`);
  return terms.join(" ");
}
